import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { citizenAPI, householdAPI, authAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Citizens.css';

const emptyForm = {
  full_name: '',
  household_link_status: '',
  household_id: '',
  relation_to_head: '',
  date_of_birth: '',
  gender: '',
  aadhaar_number: '',
  voter_id: '',
  pan_number: '',
  phone: '',
  email: '',
  education_level: '',
  occupation: '',
  is_alive: true,
  migration_status: 'resident',
  no_phone_available: false,
};

const relationOptions = [
  ['self', 'स्वतः'],
  ['spouse', 'पती/पत्नी'],
  ['son', 'मुलगा'],
  ['daughter', 'मुलगी'],
  ['father', 'वडील'],
  ['mother', 'आई'],
  ['other', 'इतर'],
];

const genderOptions = [
  ['male', 'पुरुष'],
  ['female', 'स्त्री'],
  ['other', 'इतर'],
];

const migrationOptions = [
  ['resident', 'रहिवासी'],
  ['migrated_out', 'बाहेर स्थलांतरित'],
  ['migrated_in', 'नवीन स्थलांतरित'],
];

const today = () => new Date().toISOString().slice(0, 10);
const normalizeDigits = (value, max) => String(value || '').replace(/\D/g, '').slice(0, max);
const isMaskedAadhaar = (value) => /^X{4}-X{4}-\d{4}$/.test(String(value || '').trim());
const genderLabel = (value) => genderOptions.find(([key]) => key === value)?.[1] || '-';
const relationLabel = (value) => relationOptions.find(([key]) => key === value)?.[1] || '-';
const formatDateInput = (value) => (value ? String(value).split('T')[0] : '');
const citizenCollator = new Intl.Collator('mr-IN', { numeric: true, sensitivity: 'base' });
const sortCitizenRows = (rows, sort) => [...rows].sort((a, b) => {
  const av = sort.key === 'date_of_birth' ? a.birth_year : a?.[sort.key];
  const bv = sort.key === 'date_of_birth' ? b.birth_year : b?.[sort.key];
  const result = citizenCollator.compare(String(av ?? ''), String(bv ?? ''));
  return sort.direction === 'ASC' ? result : -result;
});

const Field = ({ label, name, value, onChange, type = 'text', required = false, optional = false, isInvalid = false, errorHint, help, ...props }) => (
  <div className="form-group admin-field">
    <label htmlFor={name}>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      className={isInvalid ? 'invalid-field' : ''}
      {...props}
    />
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : help ? <small className="property-field-hint">{help}</small> : null}
  </div>
);

const SelectField = ({ label, name, value, onChange, options, required = false, optional = false, isInvalid = false, errorHint, placeholder, disabled = false }) => (
  <div className="form-group admin-field">
    <label htmlFor={name}>
      {label}
      {required && <span className="req-star" aria-hidden="true">*</span>}
      {optional && <span className="opt-badge">(ऐच्छिक)</span>}
    </label>
    <select
      id={name}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      disabled={disabled}
      className={isInvalid ? 'invalid-field' : ''}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(option => {
        const item = Array.isArray(option) ? { value: option[0], label: option[1] } : typeof option === 'string' ? { value: option, label: option } : option;
        return <option key={item.value} value={item.value}>{item.label}</option>;
      })}
    </select>
    {isInvalid && errorHint ? <small className="field-error-hint">⚠ {errorHint}</small> : null}
  </div>
);

export default function Citizens() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'citizens', user);
  const [citizens, setCitizens] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState({ type: '', text: '' });
  const [invalidFields, setInvalidFields] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [migrationFilter, setMigrationFilter] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ key: 'created_at', direction: 'DESC' });
  const [exporting, setExporting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [demographics, setDemographics] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSinglePageMode, setIsSinglePageMode] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  const steps = [
    { id: 1, title: '१. वैयक्तिक माहिती', sub: 'नाव, लिंग आणि जन्मतारीख' },
    { id: 2, title: '२. कुटुंब व संपर्क', sub: 'कुटुंब लिंक, फोन व ईमेल' },
    { id: 3, title: '३. ओळख व शिक्षण', sub: 'आधार, PAN व शिक्षण' },
  ];

  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditItem(null);
    setFormData(emptyForm);
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(1);
    setPhoneOtpSent(false);
    setPhoneOtp('');
    setPhoneVerified(false);
    setPhoneVerificationToken('');
    setOtpTimer(0);
    setIsEditingPhone(false);
  }, []);

  useBackButtonClose(showForm, closeForm);

  // Auto-clear toast popup message
  useEffect(() => {
    if (!toast.text) return;
    const duration = toast.type === 'success' ? 5000 : 7000;
    const timer = setTimeout(() => setToast({ type: '', text: '' }), duration);
    return () => clearTimeout(timer);
  }, [toast]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpTimer(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Smooth auto-scroll to invalid input
  useEffect(() => {
    if (!invalidFields.length) return;
    const firstKey = invalidFields[0];
    const element = document.getElementById(firstKey);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
    }
  }, [invalidFields, currentStep]);

  const loadDemographics = useCallback(() => {
    if (!slug) return;
    citizenAPI.getDemographics(slug)
      .then(res => setDemographics(res.data))
      .catch(() => {});
  }, [slug]);

  const loadHouseholds = useCallback(() => {
    if (!slug) return;
    householdAPI.getAll(slug, { limit: 500 })
      .then(res => setHouseholds(res.data.households || []))
      .catch(() => setHouseholds([]));
  }, [slug]);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const params = { page, limit: pageSize, sort_by: sort.key, sort_order: sort.direction };
      if (search) params.search = search;
      if (genderFilter) params.gender = genderFilter;
      if (migrationFilter) params.migration_status = migrationFilter;
      const res = await citizenAPI.getAll(slug, params);
      const nextTotalPages = Math.max(1, res.data.pagination?.totalPages || 1);
      const receivedCitizens = res.data.citizens || [];
      const filteredCitizens = migrationFilter
        ? receivedCitizens.filter(citizen => citizen.migration_status === migrationFilter)
        : receivedCitizens;
      setCitizens(sortCitizenRows(filteredCitizens, sort));
      setTotalPages(nextTotalPages);
      setTotalCount(res.data.pagination?.total || 0);
      if (page > nextTotalPages) setPage(nextTotalPages);
    } catch (err) {
      setToast({ type: 'error', text: 'नागरिक माहिती लोड करण्यात अयशस्वी' });
    } finally {
      setLoading(false);
    }
  }, [slug, page, pageSize, search, genderFilter, migrationFilter, sort]);

  useEffect(() => { loadDemographics(); }, [loadDemographics]);
  useEffect(() => { loadHouseholds(); }, [loadHouseholds]);
  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSearchChange = (val) => { setSearchInput(val); };
  const handleGenderFilterChange = (val) => { setGenderFilter(val); setPage(1); };
  const toggleSort = key => { setSort(current => ({ key, direction: current.key === key && current.direction === 'ASC' ? 'DESC' : 'ASC' })); setPage(1); };
  const exportCsv = async () => {
    if (!slug || exporting) return; setExporting(true); setToast({ type: '', text: '' });
    try {
      const params = { page: 1, limit: Math.max(totalCount, pageSize), sort_by: sort.key, sort_order: sort.direction };
      if (search) params.search = search; if (genderFilter) params.gender = genderFilter; if (migrationFilter) params.migration_status = migrationFilter;
      const { data } = await citizenAPI.getAll(slug, params);
      const exportRows = migrationFilter ? (data.citizens || []).filter(c => c.migration_status === migrationFilter) : (data.citizens || []);
      const rows = sortCitizenRows(exportRows, sort);
      const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [['नाव','कुटुंब UID','नाते','लिंग','जन्मवर्ष','फोन','आधार','स्थलांतर स्थिती'], ...rows.map(c => [c.full_name,c.Household?.household_uid,relationLabel(c.relation_to_head),genderLabel(c.gender),c.birth_year,c.phone,c.aadhaar_number,c.migration_status])].map(row => row.map(cell).join(',')).join('\n');
      const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })); const link = document.createElement('a'); link.href = url; link.download = `citizens-${new Date().toISOString().slice(0,10)}.csv`; link.click(); URL.revokeObjectURL(url);
    } catch { setToast({ type: 'error', text: 'निर्यात तयार करता आली नाही.' }); } finally { setExporting(false); }
  };

  const openCreate = () => {
    setToast({ type: '', text: '' });
    setInvalidFields([]);
    setEditItem(null);
    setFormData(emptyForm);
    setCurrentStep(1);
    setShowForm(true);
    setPhoneOtpSent(false);
    setPhoneOtp('');
    setPhoneVerified(false);
    setPhoneVerificationToken('');
    setOtpTimer(0);
    setIsEditingPhone(true);
  };

  const fillFormFromCitizen = (citizen) => {
    const isLinked = Boolean(citizen.household_id);
    setFormData({
      ...emptyForm,
      full_name: citizen.full_name || '',
      household_link_status: isLinked ? 'linked' : 'not_linked',
      household_id: citizen.household_id || '',
      relation_to_head: isLinked ? (citizen.relation_to_head || '') : '',
      date_of_birth: formatDateInput(citizen.date_of_birth),
      gender: citizen.gender || '',
      aadhaar_number: citizen.aadhaar_number || '',
      voter_id: citizen.voter_id || '',
      pan_number: citizen.pan_number || '',
      phone: citizen.phone || '',
      email: citizen.email || '',
      education_level: citizen.education_level || '',
      occupation: citizen.occupation || '',
      is_alive: citizen.is_alive !== false,
      migration_status: citizen.migration_status || 'resident',
      no_phone_available: !citizen.phone,
    });
    setPhoneVerified(Boolean(citizen.phone));
    setPhoneOtpSent(false);
    setPhoneOtp('');
    setPhoneVerificationToken('');
    setOtpTimer(0);
    setIsEditingPhone(false);
  };

  const handleSendOtp = async () => {
    const rawPhone = normalizeDigits(formData.phone, 10);
    if (!rawPhone || rawPhone.length !== 10) {
      setInvalidFields(prev => [...new Set([...prev, 'phone'])]);
      setToast({ type: 'error', text: 'कृपया १० अंकी वैध मोबाईल नंबर भरा.' });
      return;
    }
    setOtpLoading(true);
    setToast({ type: '', text: '' });
    try {
      await authAPI.requestRegistrationOtp({
        phone: rawPhone,
        full_name: formData.full_name?.trim() || 'नागरिक',
        tenant_slug: slug,
        citizen_id: editItem ? editItem.id : undefined,
      });
      setPhoneOtpSent(true);
      setPhoneVerified(false);
      setPhoneVerificationToken('');
      setOtpTimer(60);
      setToast({ type: 'success', text: 'नागरिकाच्या व्हॉट्सॲपवर ६ अंकी पडताळणी कोड (OTP) पाठवला आहे.' });
    } catch (err) {
      const isRegistered = err.response?.data?.code === 'PHONE_ALREADY_REGISTERED';
      const msg = isRegistered
        ? 'हा मोबाईल क्रमांक आधीच नोंदणीकृत आहे.'
        : (err.response?.data?.error || 'व्हॉट्सॲप OTP पाठवण्यात अयशस्वी. कृपया मोबाईल क्रमांक तपासा.');
      setToast({ type: 'error', text: msg });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const rawPhone = normalizeDigits(formData.phone, 10);
    const otp = phoneOtp.trim();
    if (!otp || otp.length !== 6) {
      setToast({ type: 'error', text: 'कृपया ६ अंकी व्हॉट्सॲप OTP टाका.' });
      return;
    }
    setOtpLoading(true);
    setToast({ type: '', text: '' });
    try {
      const res = await authAPI.verifyRegistrationOtp({
        phone: rawPhone,
        otp,
        tenant_slug: slug,
      });
      setPhoneVerificationToken(res.data.phone_verification_token);
      setPhoneVerified(true);
      setInvalidFields(prev => prev.filter(f => f !== 'phone' && f !== 'phone_verification'));
      setToast({ type: 'success', text: 'मोबाईल क्रमांक यशस्वीरित्या पडताळला गेला!' });
    } catch (err) {
      setToast({ type: 'error', text: err.response?.data?.error || 'अवैध OTP कोड. कृपया पुन्हा प्रयत्न करा.' });
    } finally {
      setOtpLoading(false);
    }
  };

  const validateStep1 = useCallback(() => {
    const missing = [];
    if (!formData.full_name || !formData.full_name.trim()) missing.push('full_name');
    if (!formData.gender) missing.push('gender');
    if (!formData.date_of_birth) {
      missing.push('date_of_birth');
    } else if (new Date(formData.date_of_birth) > new Date()) {
      missing.push('date_of_birth');
    }
    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? (formData.date_of_birth && new Date(formData.date_of_birth) > new Date() ? 'जन्मतारीख भविष्यातील असू शकत नाही.' : 'वैयक्तिक माहितीतील सर्व अनिवार्य बाबी भरा.') : ''
    };
  }, [formData.full_name, formData.gender, formData.date_of_birth]);

  const validateStep2 = useCallback(() => {
    const missing = [];
    const phone = normalizeDigits(formData.phone, 10);
    const email = (formData.email || '').trim();
    const originalPhone = editItem?.phone ? normalizeDigits(editItem.phone, 10) : '';
    const isPhoneUnchanged = Boolean(originalPhone && phone === originalPhone);
    const isPhoneVerifiedNow = isPhoneUnchanged || phoneVerified;

    if (!formData.household_link_status) missing.push('household_link_status');
    if (formData.household_link_status === 'linked') {
      if (!formData.household_id) missing.push('household_id');
      if (!formData.relation_to_head) missing.push('relation_to_head');
    }

    if (!formData.no_phone_available) {
      if (!phone || phone.length !== 10) missing.push('phone');
      if (!isPhoneVerifiedNow) missing.push('phone_verification');
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      missing.push('email');
    }

    let errorText = '';
    if (missing.length > 0) {
      if (missing.includes('household_link_status')) {
        errorText = 'कुटुंब लिंक स्थिती निवडणे आवश्यक आहे.';
      } else if (missing.includes('household_id') || missing.includes('relation_to_head')) {
        errorText = 'कुटुंब विषयक माहिती पूर्ण भरा.';
      } else if (missing.includes('phone')) {
        errorText = '१० अंकी वैध मोबाईल नंबर टाका.';
      } else if (missing.includes('phone_verification')) {
        errorText = 'कृपया बदललेल्या मोबाईल क्रमांकाची व्हॉट्सॲप OTP द्वारे पडताळणी करा.';
      } else if (missing.includes('email')) {
        errorText = 'वैध ईमेल पत्ता टाका.';
      } else {
        errorText = 'कुटुंब व संपर्कातील सर्व अनिवार्य बाबी भरा.';
      }
    }

    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText,
    };
  }, [formData.household_link_status, formData.household_id, formData.relation_to_head, formData.no_phone_available, formData.phone, formData.email, editItem, phoneVerified]);

  const validateStep3 = useCallback(() => {
    const missing = [];
    const aadhaar = isMaskedAadhaar(formData.aadhaar_number) ? '000000000000' : normalizeDigits(formData.aadhaar_number, 12);
    const pan = (formData.pan_number || '').trim().toUpperCase();

    if (!aadhaar || aadhaar.length !== 12) missing.push('aadhaar_number');
    if (!formData.education_level || !formData.education_level.trim()) missing.push('education_level');
    if (pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) missing.push('pan_number');

    return {
      isValid: missing.length === 0,
      missingFields: missing,
      errorText: missing.length > 0 ? (!aadhaar || aadhaar.length !== 12 ? '१२ अंकी आधार क्रमांक टाका.' : pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan) ? 'PAN क्रमांक ABCDE1234F स्वरूपात असावा.' : 'ओळख व शिक्षणातील सर्व अनिवार्य बाबी भरा.') : ''
    };
  }, [formData.aadhaar_number, formData.education_level, formData.pan_number]);

  const checkStepValidity = useCallback((stepNo) => {
    if (stepNo === 1) return validateStep1();
    if (stepNo === 2) return validateStep2();
    if (stepNo === 3) return validateStep3();
    return { isValid: true, missingFields: [], errorText: '' };
  }, [validateStep1, validateStep2, validateStep3]);

  const handleStepClick = (targetStep) => {
    if (targetStep <= currentStep) {
      setCurrentStep(targetStep);
      return;
    }
    for (let s = 1; s < targetStep; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setToast({ type: 'error', text: `पायरी ${s} अपूर्ण आहे: ${check.errorText}` });
        setCurrentStep(s);
        return;
      }
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(targetStep);
  };

  const handleNextStep = () => {
    const check = checkStepValidity(currentStep);
    if (!check.isValid) {
      setInvalidFields(check.missingFields);
      return setToast({ type: 'error', text: check.errorText });
    }
    setInvalidFields([]);
    setToast({ type: '', text: '' });
    setCurrentStep(prev => Math.min(steps.length, prev + 1));
  };

  const buildPayload = () => {
    const { household_link_status, ...payload } = formData;
    const isLinked = household_link_status === 'linked';
    return {
      ...payload,
      full_name: formData.full_name.trim(),
      household_id: isLinked ? (formData.household_id || null) : null,
      relation_to_head: isLinked ? (formData.relation_to_head || null) : null,
      no_phone_available: Boolean(formData.no_phone_available),
      phone: formData.no_phone_available ? null : (normalizeDigits(formData.phone, 10) || null),
      phone_verification_token: formData.no_phone_available ? undefined : (phoneVerificationToken || undefined),
      aadhaar_number: normalizeDigits(formData.aadhaar_number, 12) || null,
      pan_number: formData.pan_number.trim().toUpperCase() || null,
      email: formData.no_phone_available ? null : (formData.email.trim() || null),
      voter_id: formData.voter_id.trim() || null,
      education_level: formData.education_level.trim() || null,
      occupation: formData.occupation.trim() || null,
      date_of_birth: formData.date_of_birth || null,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (let s = 1; s <= 3; s++) {
      const check = checkStepValidity(s);
      if (!check.isValid) {
        setInvalidFields(check.missingFields);
        setCurrentStep(s);
        return setToast({ type: 'error', text: `पायरी ${s} मधील त्रुटी: ${check.errorText}` });
      }
    }

    setSaving(true);
    setToast({ type: '', text: '' });

    try {
      const payload = buildPayload();
      if (editItem) {
        await citizenAPI.update(slug, editItem.id, payload);
        setToast({ type: 'success', text: 'नागरिक माहिती अद्ययावत केली' });
      } else {
        const res = await citizenAPI.create(slug, payload);
        if (res.data?.user_created) {
          setToast({
            type: 'success',
            text: 'नागरिक नोंद व लॉगिन यशस्वी! लॉगिन तपशील नागरिकाच्या व्हॉट्सॲपवर पाठवले आहेत.',
          });
        } else {
          setToast({ type: 'success', text: 'नागरिक नोंद यशस्वीपणे जोडली' });
        }
      }
      closeForm();
      await Promise.all([loadData(), loadDemographics(), loadHouseholds()]);
    } catch (err) {
      setToast({ type: 'error', text: err.response?.data?.error || 'नागरिक जतन करण्यात अयशस्वी' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (item) => {
    setToast({ type: '', text: '' });
    setInvalidFields([]);
    setFormLoading(true);
    try {
      const res = await citizenAPI.getById(slug, item.id);
      const citizen = res.data.citizen || item;
      setEditItem(citizen);
      fillFormFromCitizen(citizen);
      setCurrentStep(1);
      setShowForm(true);
    } catch (err) {
      setToast({ type: 'error', text: err.response?.data?.error || 'नागरिक तपशील लोड करण्यात अयशस्वी' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvalidFields(current => current.filter(item => {
      if (name === 'household_link_status' && value === 'not_linked') {
        if (item === 'household_id' || item === 'relation_to_head') return false;
      }
      return item !== name && (item !== 'phone_verification' || name !== 'phone');
    }));

    let nextValue = value;
    if (name === 'phone') {
      nextValue = normalizeDigits(value, 10);
      const origPhone = editItem?.phone ? normalizeDigits(editItem.phone, 10) : '';
      if (origPhone && nextValue === origPhone) {
        setInvalidFields(prev => prev.filter(f => f !== 'phone_verification'));
      } else if (phoneVerified) {
        setPhoneVerified(false);
        setPhoneVerificationToken('');
        setPhoneOtpSent(false);
        setPhoneOtp('');
      }
    } else if (name === 'aadhaar_number') {
      nextValue = normalizeDigits(value, 12);
    } else if (name === 'pan_number') {
      nextValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    }

    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : nextValue };
      if (name === 'household_link_status' && value === 'not_linked') {
        next.household_id = '';
        next.relation_to_head = '';
      }
      if (name === 'household_id' && value) next.household_link_status = 'linked';
      return next;
    });
  };

  const rawPhone = normalizeDigits(formData.phone, 10);
  const originalPhone = editItem?.phone ? normalizeDigits(editItem.phone, 10) : '';
  const isPhoneUnchanged = Boolean(originalPhone && rawPhone === originalPhone);
  const isPhoneVerifiedNow = isPhoneUnchanged || phoneVerified;
  const isPhoneFieldDisabled = Boolean(!formData.no_phone_available && ((!isEditingPhone && isPhoneUnchanged) || phoneVerified));

  return (
    <div className="admin-page-shell citizen-page-shell">
      {/* Global Floating Toast Popup */}
      {toast.text && (
        <div className="gp-toast-container">
          <div className={`gp-toast gp-toast-${toast.type || 'info'}`} role="alert">
            <span className="gp-toast-icon">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <div className="gp-toast-content">
              <div className="gp-toast-title">
                {toast.type === 'success' ? 'यशस्वी क्रिया' : toast.type === 'error' ? 'फॉर्म अपूर्ण / त्रुटी' : 'माहिती'}
              </div>
              <div className="gp-toast-message">{toast.text}</div>
            </div>
            <button
              type="button"
              className="gp-toast-close"
              onClick={() => setToast({ type: '', text: '' })}
              title="बंद करा"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="admin-card-header admin-page-header citizen-page-header">
        <div className="citizen-heading-block">
          <span className="citizen-heading-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.5"/><path d="M3.5 20v-2a5.5 5.5 0 0 1 11 0v2M14 14.5a4.5 4.5 0 0 1 6.5 4V20"/></svg></span>
          <div>
            <span className="citizen-heading-eyebrow">नागरिक नोंदणी व KYC</span>
            <h2>नागरिक व्यवस्थापन</h2>
            <p>ग्रामपंचायत क्षेत्रातील नागरिकांची ओळख, संपर्क आणि कुटुंबाशी जोडलेली माहिती व्यवस्थापित करा.</p>
          </div>
        </div>
        {demographics && (
          <div className="citizen-demo-strip">
            <div className="demo-chip total"><i>∑</i>
              <span className="demo-chip-value">{demographics.total}</span>
              <span className="demo-chip-label">एकूण</span>
            </div>
            {(demographics.genderBreakdown || []).map(g => (
              <div key={g.gender} className={`demo-chip gender-${g.gender}`}><i>{g.gender === 'male' ? '♂' : g.gender === 'female' ? '♀' : '◇'}</i>
                <span className="demo-chip-value">{g.count}</span>
                <span className="demo-chip-label">{genderLabel(g.gender)}</span>
              </div>
            ))}
          </div>
        )}
        {canWriteThis && (
          <button className="btn citizen-add-btn" onClick={showForm ? closeForm : openCreate} disabled={formLoading}>
            {showForm ? 'रद्द करा' : '+ नागरिक जोडा'}
          </button>
        )}
      </div>

      {showForm ? (
        <div className="admin-card admin-form-card">
          <div className="admin-form-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span className="admin-form-eyebrow">{editItem ? 'संपादन' : 'नवीन नोंद'}</span>
              <h2>{editItem ? 'नागरिक माहिती संपादित करा' : 'नवीन नागरिक नोंदणी'}</h2>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                className="citizen-stepper-toggle"
                onClick={() => setIsSinglePageMode(!isSinglePageMode)}
              >
                {isSinglePageMode ? '🧩 बहु-पायरी फॉर्मवर जा (Multi-Step Wizard)' : '📄 सर्व विभाग एकत्र पहा (Full Page)'}
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={closeForm}>यादीकडे परत</button>
            </div>
          </div>

          {/* Stepper Header */}
          {!isSinglePageMode && (
            <div className="citizen-stepper-container" style={{ marginTop: 16 }}>
              <div className="citizen-stepper-header">
                <span>पायरी {currentStep} / {steps.length}: <strong>{steps[currentStep - 1]?.title}</strong></span>
              </div>
              <div className="citizen-stepper">
                {steps.map((step, idx) => {
                  const stepNum = idx + 1;
                  const isActive = currentStep === stepNum;
                  const isCompleted = currentStep > stepNum;
                  const isLocked = stepNum > currentStep && !checkStepValidity(stepNum - 1).isValid;
                  return (
                    <button
                      key={step.id}
                      type="button"
                      className={`citizen-stepper-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'restricted' : ''}`}
                      onClick={() => handleStepClick(stepNum)}
                      title={isLocked ? `पायरी ${stepNum - 1} अपूर्ण आहे` : step.title}
                    >
                      <span className="citizen-step-num">{isCompleted ? '✓' : isLocked ? '🔒' : stepNum}</span>
                      <div className="citizen-step-label">
                        <strong>{step.title}</strong>
                        <small>{step.sub}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Step 1: वैयक्तिक माहिती */}
            {(isSinglePageMode || currentStep === 1) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <h3 style={{ margin: '0 0 14px', color: '#173d5d', fontSize: '1.05rem' }}>१. वैयक्तिक माहिती</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 15 }}>
                  <Field
                    label="पूर्ण नाव"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    isInvalid={invalidFields.includes('full_name')}
                    errorHint="पूर्ण नाव आवश्यक आहे."
                  />
                  <SelectField
                    label="लिंग"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    options={genderOptions}
                    required
                    placeholder="लिंग निवडा"
                    isInvalid={invalidFields.includes('gender')}
                    errorHint="लिंग निवडणे आवश्यक आहे."
                  />
                  <Field
                    label="जन्मतारीख"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    max={today()}
                    required
                    isInvalid={invalidFields.includes('date_of_birth')}
                    errorHint="जन्मतारीख आवश्यक असून ती भविष्यातील असू शकत नाही."
                  />
                  <div className="form-group admin-field" style={{ display: 'flex', alignItems: 'center', paddingTop: 28 }}>
                    <label className="admin-check-row" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 650, color: '#3d566b' }}>
                      <input type="checkbox" name="is_alive" checked={formData.is_alive} onChange={handleChange} style={{ width: 18, height: 18 }} />
                      सक्रिय / जिवंत नोंद
                    </label>
                  </div>
                </div>
              </section>
            )}

            {/* Step 2: कुटुंब आणि संपर्क */}
            {(isSinglePageMode || currentStep === 2) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <h3 style={{ margin: '0 0 14px', color: '#173d5d', fontSize: '1.05rem' }}>२. कुटुंब आणि संपर्क माहिती</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 15 }}>
                  <div style={{ gridColumn: formData.household_link_status === 'linked' ? 'auto' : 'span 2' }}>
                    <SelectField
                      label="कुटुंबाशी लिंक स्थिती"
                      name="household_link_status"
                      value={formData.household_link_status}
                      onChange={handleChange}
                      options={[['linked', 'कुटुंबाशी लिंक आहे'], ['not_linked', 'कुटुंबाशी लिंक नाही']]}
                      required
                      placeholder="स्थिती निवडा"
                      isInvalid={invalidFields.includes('household_link_status')}
                      errorHint="कुटुंब लिंक स्थिती निवडणे आवश्यक आहे."
                    />
                  </div>

                  {formData.household_link_status === 'linked' && (
                    <>
                      <SelectField
                        label="कुटुंब निवडा"
                        name="household_id"
                        value={formData.household_id}
                        onChange={handleChange}
                        options={households.map(h => [h.id, `${h.household_uid} - ${h.head_name}`])}
                        required
                        placeholder="कुटुंब निवडा"
                        isInvalid={invalidFields.includes('household_id')}
                        errorHint="लिंक केलेल्या नागरिकासाठी कुटुंब निवडणे आवश्यक आहे."
                      />
                      <div style={{ gridColumn: 'span 2' }}>
                        <SelectField
                          label="कुटुंबप्रमुखाशी नाते"
                          name="relation_to_head"
                          value={formData.relation_to_head}
                          onChange={handleChange}
                          options={relationOptions}
                          required
                          placeholder="नाते निवडा"
                          isInvalid={invalidFields.includes('relation_to_head')}
                          errorHint="नाते निवडणे आवश्यक आहे."
                        />
                      </div>
                    </>
                  )}

                  {/* Missing Phone Checkbox for Minors / Dependents */}
                  <div style={{ gridColumn: 'span 2', background: '#f8fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 650, color: '#1e293b' }}>
                      <input
                        type="checkbox"
                        name="no_phone_available"
                        checked={formData.no_phone_available}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            no_phone_available: isChecked,
                            phone: isChecked ? '' : prev.phone,
                            email: isChecked ? '' : prev.email,
                          }));
                          if (isChecked) {
                            setPhoneOtpSent(false);
                            setPhoneOtp('');
                            setPhoneVerified(false);
                            setPhoneVerificationToken('');
                            setInvalidFields(prev => prev.filter(f => f !== 'phone' && f !== 'phone_verification'));
                          }
                        }}
                        style={{ width: 18, height: 18, accentColor: '#2563eb' }}
                      />
                      <span>मोबाईल नंबर उपलब्ध नाही (अल्पवयीन / अवलंबित नागरिक)</span>
                    </label>
                    <p style={{ margin: '4px 0 0 28px', fontSize: '0.84rem', color: '#64748b', lineHeight: 1.4 }}>
                      {formData.no_phone_available
                        ? 'ℹ मोबाईल नसल्यामुळे या नागरिकाचे लॉगिन खाते तयार होणार नाही. मात्र अधिकृत नागरिक नोंद तयार होईल व मिळकत/व्यवसाय मालक किंवा वारसदार म्हणून जोडता येईल.'
                        : 'ℹ मोबाईल नंबर दिल्यास नागरिकाचे नागरिक लॉगिन खाते थेट तयार होईल आणि सुरक्षित लॉगिन तपशील व्हॉट्सॲपवर पाठवले जातील.'}
                    </p>
                  </div>

                  {!formData.no_phone_available && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 280px' }}>
                          <Field
                            label="मोबाईल नंबर (WhatsApp)"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            maxLength={10}
                            required
                            isInvalid={invalidFields.includes('phone')}
                            errorHint="१० अंकी वैध मोबाईल क्रमांक टाका."
                            placeholder="10 अंकी मोबाईल क्रमांक"
                            inputMode="tel"
                            disabled={isPhoneFieldDisabled}
                            style={isPhoneFieldDisabled ? { background: '#f8fafc', color: '#334155', cursor: 'not-allowed', fontWeight: 600 } : {}}
                          />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          {isPhoneFieldDisabled ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: 8, fontWeight: 700, fontSize: '0.88rem' }}>
                                ✓ पडताळणी पूर्ण
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => {
                                  setIsEditingPhone(true);
                                  setPhoneVerified(false);
                                  setPhoneOtpSent(false);
                                  setPhoneOtp('');
                                  setPhoneVerificationToken('');
                                  setInvalidFields(prev => prev.filter(f => f !== 'phone_verification'));
                                }}
                                style={{ whiteSpace: 'nowrap', height: 42, padding: '0 14px', fontSize: '0.88rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                title="मोबाईल क्रमांक बदला"
                              >
                                ✏ नंबर बदला
                              </button>
                            </div>
                          ) : isPhoneUnchanged ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 600, fontSize: '0.88rem' }}>
                                ✓ मूळ क्रमांक कायम
                              </span>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => {
                                  setIsEditingPhone(false);
                                  setInvalidFields(prev => prev.filter(f => f !== 'phone_verification'));
                                }}
                                style={{ whiteSpace: 'nowrap', height: 42, padding: '0 14px', fontSize: '0.88rem', fontWeight: 600 }}
                              >
                                ✕ बदल रद्द करा
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={handleSendOtp}
                                disabled={otpLoading || (formData.phone || '').length !== 10 || otpTimer > 0}
                                style={{ whiteSpace: 'nowrap', height: 42, padding: '0 16px', fontWeight: 600 }}
                              >
                                {otpLoading ? 'पाठवत आहे…' : otpTimer > 0 ? `पुन्हा पाठवा (${otpTimer}s)` : phoneOtpSent ? 'पुन्हा OTP पाठवा' : 'WhatsApp OTP पाठवा'}
                              </button>
                              {originalPhone && (
                                <button
                                  type="button"
                                  className="btn btn-outline"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, phone: originalPhone }));
                                    setIsEditingPhone(false);
                                    setPhoneOtpSent(false);
                                    setPhoneOtp('');
                                    setPhoneVerificationToken('');
                                    setInvalidFields(prev => prev.filter(f => f !== 'phone_verification'));
                                  }}
                                  style={{ whiteSpace: 'nowrap', height: 42, padding: '0 12px', fontSize: '0.86rem', color: '#64748b' }}
                                  title="मूळ क्रमांक ठेवा"
                                >
                                  ✕ मूळ क्रमांक ठेवा
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {phoneOtpSent && !isPhoneVerifiedNow && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 4, marginBottom: 16, background: '#eff6ff', padding: '12px 16px', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                          <input
                            type="text"
                            maxLength={6}
                            placeholder="६ अंकी OTP"
                            value={phoneOtp}
                            onChange={(e) => setPhoneOtp(normalizeDigits(e.target.value, 6))}
                            style={{ width: 150, height: 40, padding: '0 12px', borderRadius: 6, border: '1px solid #93c5fd', fontSize: '1.05rem', letterSpacing: 3, fontWeight: 700, textAlign: 'center' }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                              handleVerifyOtp().then(() => {
                                setIsEditingPhone(false);
                              });
                            }}
                            disabled={otpLoading || phoneOtp.length !== 6}
                            style={{ height: 40, padding: '0 18px' }}
                          >
                            {otpLoading ? 'पडताळत आहे…' : 'OTP पडताळा'}
                          </button>
                          <span style={{ fontSize: '0.84rem', color: '#1e40af' }}>
                            नागरिकाच्या {formData.phone} या WhatsApp क्रमांकावर आलेला ६ अंकी OTP टाका.
                          </span>
                        </div>
                      )}
                      {invalidFields.includes('phone_verification') && !isPhoneVerifiedNow && (
                        <small className="field-error-hint" style={{ display: 'block', marginTop: -10, marginBottom: 12 }}>
                          ⚠ कृपया बदललेल्या मोबाईल क्रमांकाची व्हॉट्सॲप OTP पडताळणी पूर्ण करा.
                        </small>
                      )}
                    </div>
                  )}

                  <div style={{ gridColumn: 'span 2' }}>
                    <Field
                      label="ईमेल"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      optional={true}
                      isInvalid={invalidFields.includes('email')}
                      errorHint="वैध ईमेल पत्ता भरा (उदा. name@domain.com)."
                      placeholder="उदा. citizen@domain.com (ऐच्छिक)"
                      disabled={formData.no_phone_available}
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Step 3: ओळख आणि इतर तपशील */}
            {(isSinglePageMode || currentStep === 3) && (
              <section className="property-form-card" style={{ padding: 18, marginTop: 16 }}>
                <h3 style={{ margin: '0 0 14px', color: '#173d5d', fontSize: '1.05rem' }}>३. ओळख, शिक्षण आणि स्थलांतर</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 15 }}>
                  <Field
                    label="आधार क्रमांक"
                    name="aadhaar_number"
                    value={formData.aadhaar_number}
                    onChange={handleChange}
                    maxLength={12}
                    required
                    isInvalid={invalidFields.includes('aadhaar_number')}
                    errorHint="१२ अंकी वैध आधार क्रमांक टाका."
                    placeholder="12 अंकी आधार क्रमांक"
                    inputMode="numeric"
                  />
                  <Field
                    label="मतदार ओळखपत्र"
                    name="voter_id"
                    value={formData.voter_id}
                    onChange={handleChange}
                    optional
                  />
                  <Field
                    label="PAN"
                    name="pan_number"
                    value={formData.pan_number}
                    onChange={handleChange}
                    maxLength={10}
                    optional
                    isInvalid={invalidFields.includes('pan_number')}
                    errorHint="PAN स्वरुपनियम ABCDE1234F असे असावे."
                    placeholder="ABCDE1234F"
                  />
                  <SelectField
                    label="स्थलांतर स्थिती"
                    name="migration_status"
                    value={formData.migration_status}
                    onChange={handleChange}
                    options={migrationOptions}
                    required
                  />
                  <Field
                    label="शिक्षण"
                    name="education_level"
                    value={formData.education_level}
                    onChange={handleChange}
                    required
                    isInvalid={invalidFields.includes('education_level')}
                    errorHint="शिक्षणाची माहिती भरणे आवश्यक आहे."
                  />
                  <Field
                    label="व्यवसाय"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    optional
                  />
                </div>
              </section>
            )}

            {/* Stepper Action Bar */}
            {!isSinglePageMode && (
              <div className="citizen-step-actions">
                <button
                  type="button"
                  className="btn-prev"
                  disabled={currentStep === 1}
                  onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                >
                  ← मागील पायरी
                </button>

                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : editItem ? 'अद्ययावत करा' : 'मसुदा जतन करा'}
                </button>

                {currentStep < steps.length ? (
                  <button
                    type="button"
                    className="btn-next"
                    onClick={handleNextStep}
                  >
                    पुढील पायरी →
                  </button>
                ) : null}
              </div>
            )}

            {isSinglePageMode && (
              <div className="admin-form-actions" style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="btn-spinner"></span> जतन होत आहे…</> : editItem ? 'अद्ययावत करा' : 'नागरिक तयार करा'}
                </button>
                <button type="button" className="btn btn-outline" onClick={closeForm}>रद्द करा</button>
              </div>
            )}
          </form>
        </div>
      ) : (
        <div className="admin-card admin-table-card">
          <div className="citizen-table-toolbar">
            <div className="citizen-search"><span className="notranslate" translate="no">⌕</span><input placeholder="नाव, मोबाईल किंवा आधार क्रमांकाने शोधा..." value={searchInput} onChange={e => handleSearchChange(e.target.value)} /></div>
            <select value={genderFilter} onChange={e => handleGenderFilterChange(e.target.value)}><option value="">सर्व लिंग</option>{genderOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            <select value={migrationFilter} onChange={e => { setMigrationFilter(e.target.value); setPage(1); }}><option value="">सर्व रहिवासी</option>{migrationOptions.map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select>
            <button className="citizen-export-btn" onClick={exportCsv} disabled={exporting}>⇩ {exporting ? 'तयार होत आहे...' : 'CSV निर्यात'}</button>
          </div>
          <div className="citizen-table-meta"><span><strong>{totalCount}</strong> <span>नागरिक नोंदी</span></span><span className={`citizen-load-status ${loading ? 'visible' : ''}`}>{loading ? <span><i /> <span>माहिती अपडेट होत आहे</span></span> : null}</span></div>
          <div className={`table-scroll citizen-table-scroll ${loading ? 'is-refreshing' : ''}`} aria-busy={loading}>
            <table className="data-table citizen-data-table">
              <thead>
                <tr>
                  <CitizenSortHeader label="नाव" column="full_name" sort={sort} onSort={toggleSort} />
                  <CitizenSortHeader label="कुटुंब" column="household_id" sort={sort} onSort={toggleSort} />
                  <CitizenSortHeader label="नाते" column="relation_to_head" sort={sort} onSort={toggleSort} />
                  <CitizenSortHeader label="लिंग" column="gender" sort={sort} onSort={toggleSort} />
                  <CitizenSortHeader label="जन्मवर्ष" column="date_of_birth" sort={sort} onSort={toggleSort} />
                  <CitizenSortHeader label="फोन" column="phone" sort={sort} onSort={toggleSort} />
                  <th><span>आधार</span></th>
                  <th><span>कृती</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && citizens.length === 0 ? (
                  <LoadingSpinner.TableRow colSpan={8} message="नागरिक माहिती लोड होत आहे..." />
                ) : citizens.length === 0 ? (
                  <tr><td colSpan="8"><span>नागरिक नोंदी आढळल्या नाहीत.</span></td></tr>
                ) : citizens.map((c) => (
                  <tr key={c.id}>
                    <td><div className={`citizen-name-cell gender-${c.gender || 'other'}`}><span>{(c.full_name || '?').charAt(0)}</span><div><strong>{c.full_name}</strong><small>{c.occupation || c.email || 'नागरिक नोंद'}</small></div></div></td>
                    <td><span className={`citizen-household-pill ${c.Household ? 'linked' : ''}`}>{c.Household?.household_uid || 'लिंक नाही'}</span></td>
                    <td><span className="citizen-relation">{relationLabel(c.relation_to_head)}</span></td>
                    <td><span className={`citizen-gender-pill gender-${c.gender}`}>{genderLabel(c.gender)}</span></td>
                    <td><span>{c.birth_year || '-'}</span></td>
                    <td><span>{c.phone || '-'}</span></td>
                    <td><span>{c.aadhaar_number || '-'}</span></td>
                    <td>{canWriteThis && <button className="citizen-edit-btn" onClick={() => handleEdit(c)} disabled={formLoading}><span>संपादित करा</span> <span className="notranslate" translate="no">›</span></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="table-pagination citizen-pagination">
            <label><span>प्रति पृष्ठ</span> <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}><option value="10">10</option><option value="20">20</option><option value="50">50</option></select></label>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><span className="notranslate" translate="no">←</span> <span>मागील</span></button>
            <span><span>पृष्ठ</span> <strong>{page}</strong> <span>/</span> <span>{totalPages}</span></span>
            <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}><span>पुढील</span> <span className="notranslate" translate="no">→</span></button>
          </div>
        </div>
      )}
    </div>
  );
}

function CitizenSortHeader({ label, column, sort, onSort }) { const active = sort.key === column; return <th><button className={`citizen-sort-header ${active ? 'active' : ''}`} onClick={() => onSort(column)}>{label}<span>{active ? (sort.direction === 'ASC' ? '↑' : '↓') : '↕'}</span></button></th>; }
